import moment from 'moment';

import {ProjectMember as ProjectMemberFull} from 'common/types/app-api/project-member';
import {Translatable} from 'common/i18n';

export enum SubscribeSendMethod {
  Email = `Email`,
  Slack = `Slack`,
}

export const SubscribeSendMethodLabel: {[key in SubscribeSendMethod]: Translatable} = {
  [SubscribeSendMethod.Email]: ({t}) => t(`subscriptions.alert-email`),
  [SubscribeSendMethod.Slack]: ({t}) => t(`subscriptions.alert-slack`),
};

export enum SubscribeFrequency {
  Daily = `Daily`,
  Weekly = `Weekly`,
  Monthly = `Monthly`,
}

export enum SubscribeWeekDays {
  Sunday = `Sunday`,
  Monday = `Monday`,
  Tuesday = `Tuesday`,
  Wednesday = `Wednesday`,
  Thursday = `Thursday`,
  Friday = `Friday`,
  Saturday = `Saturday`,
}

// Start with Monday to match "Start of week" in project settings
export const SUBSCRIBE_WEEK_DAYS = [
  SubscribeWeekDays.Monday,
  SubscribeWeekDays.Tuesday,
  SubscribeWeekDays.Wednesday,
  SubscribeWeekDays.Thursday,
  SubscribeWeekDays.Friday,
  SubscribeWeekDays.Saturday,
  SubscribeWeekDays.Sunday,
];

export const SUBSCRIBE_WEEK_DAY_LABEL: {[key in SubscribeWeekDays]: Translatable} = {
  [SubscribeWeekDays.Monday]: ({t}) => t(`reports.day-of-week.monday`),
  [SubscribeWeekDays.Tuesday]: ({t}) => t(`reports.day-of-week.tuesday`),
  [SubscribeWeekDays.Wednesday]: ({t}) => t(`reports.day-of-week.wednesday`),
  [SubscribeWeekDays.Thursday]: ({t}) => t(`reports.day-of-week.thursday`),
  [SubscribeWeekDays.Friday]: ({t}) => t(`reports.day-of-week.friday`),
  [SubscribeWeekDays.Saturday]: ({t}) => t(`reports.day-of-week.saturday`),
  [SubscribeWeekDays.Sunday]: ({t}) => t(`reports.day-of-week.sunday`),
};

export const enum LoadingStatus {
  Idle = `idle`,
  Success = `success`,
  Failed = `failed`,
  Pending = `pending`,
}

export enum DigestRecurrence {
  Yearly = 0,
  Monthly,
  Weekly,
  Daily,
  ASAP,
  Quarterly,
}

export enum DigestDateOrdinal {
  First = 0,
  Last = -1,
}

export const DIGEST_DATE_ORDINAL_FRIENDLY_NAME: {[digestDayNumber: number]: Translatable} = {
  [DigestDateOrdinal.First]: ({t}) => t(`subscriptions.first`),
  [DigestDateOrdinal.Last]: ({t}) => t(`subscriptions.last`),
};

export interface DashboardDigestSlackSubscription {
  slack_team_id: string;
  slack_channel_name: string;
  slack_channel_id: string;
}

export interface DashboardDigest {
  created: string;
  creator_email: string;
  creator_id: number;
  confirmed_date: string;
  dashboard_creator_email: string;
  dashboard_foreign_private: boolean;
  dashboard_has_owner_with_permissions: boolean;
  dashboard_id: number;
  dashboard_title: string;
  digest_has_owner_with_permissions: boolean;
  id: number;
  monthly_week_ordinal: number;
  name: string;
  paused: boolean;
  project_id: number;

  recipients: string[];
  recur?: DigestRecurrence;
  schedule?: string;
  slack_subscriptions: DashboardDigestSlackSubscription[];
  start_date?: moment.Moment;
  status?: string;
  timezone?: string;
  workspace_id?: number;
  workspace_name?: string;
}

export interface DashboardDigestServer extends Omit<DashboardDigest, 'start_date'> {
  start_date?: string;
}

export type SerializedDashboardDigest = Pick<
  DashboardDigest,
  `monthly_week_ordinal` | `recur` | `timezone` | `slack_subscriptions` | `recipients` | `dashboard_id`
> & {
  start_date: string;
};

export const DIGEST_RECURRENCE_FRIENDLY_NAME: {[digestRecurrence: number]: Translatable} = {
  [DigestRecurrence.Daily]: ({t}) => t(`subscriptions.repeat-daily`),
  [DigestRecurrence.ASAP]: ({t}) => t(`subscriptions.repeat-asap`),
  [DigestRecurrence.Weekly]: ({t}) => t(`subscriptions.repeat-weekly`),
  [DigestRecurrence.Monthly]: ({t}) => t(`subscriptions.repeat-monthly`),
  [DigestRecurrence.Quarterly]: ({t}) => t(`subscriptions.repeat-quarterly`),
};

export enum ListOrCreateEditView {
  List = `list`,
  CreateEdit = `create-edit`,
}

export const LOADING_STATES = [LoadingStatus.Pending, LoadingStatus.Idle];

export enum DigestType {
  Email = `email`,
  Slack = `slack`,
}

export const DIGEST_DATE_FORMAT = `YYYY-MM-DDTHH:mm:ss`;

export type ProjectMember = Pick<ProjectMemberFull, 'id' | 'name' | 'email' | 'role'>;
